import { IUpdatedWidget } from "@hooks/useWidgets";
import { calculateIncrease } from "@pages/AdsAnalitics/components/Campaigns/utils";
import { Metrics } from "@pages/Dashboard/store/math.state";

const safeNumber = (value: any): number =>
  typeof value === "number" ? value : 0;

interface WidgetDef {
  id: string;
  metricKey: keyof Metrics;
  originalKey?: keyof Metrics;
  format: (value: any) => string;
  diffType: "difference" | "compare";
  compareKey?: string;
  baseMetricKey?: keyof Metrics;
}

const widgetDefs: WidgetDef[] = [
  {
    id: "2",
    metricKey: "salesTotalData",
    originalKey: "aovData",
    format: (v) => (v != null ? `$${Math.floor(v)}` : "N/A"),
    diffType: "difference",
  },
  {
    id: "3",
    metricKey: "adsImpressionsData",
    format: (v) => (v != null ? v.toString() : "N/A"),
    diffType: "compare",
    compareKey: "salesCompare",
    baseMetricKey: "salesTotalData",
  },
  {
    id: "4",
    metricKey: "adsClicksData",
    format: (v) => (v != null ? v.toString() : "N/A"),
    diffType: "compare",
    compareKey: "clicksCompare",
  },
  {
    id: "5",
    metricKey: "adsOrdersData",
    format: (v) => (v != null ? v.toString() : "N/A"),
    diffType: "compare",
    compareKey: "ordersCompare",
  },
  {
    id: "6",
    metricKey: "adsUnitsSoldData",
    format: (v) => (v != null ? v.toString() : "N/A"),
    diffType: "compare",
    compareKey: "unitSoldsCompare",
  },
  {
    id: "7",
    metricKey: "adsSalesData",
    format: (v) => (v != null ? `$${Math.floor(v)}` : "N/A"),
    diffType: "compare",
    compareKey: "salesCompare",
  },
  {
    id: "8",
    metricKey: "adsSpendData",
    format: (v) => (v != null ? `$${Math.floor(v)}` : "N/A"),
    diffType: "compare",
    compareKey: "spendCompare",
  },
  {
    id: "9",
    metricKey: "adsAcosData",
    format: (v) => (v != null ? `${v}%` : "N/A"),
    diffType: "compare",
    compareKey: "acosCompare",
  },
  {
    id: "10",
    metricKey: "adsRoasData",
    format: (v) => (v != null ? v.toString() : "N/A"),
    diffType: "compare",
    compareKey: "roasCompare",
  },
  {
    id: "11",
    metricKey: "adsCpcData",
    format: (v) => (v != null ? `$${v}` : "N/A"),
    diffType: "compare",
    compareKey: "cpcCompare",
  },
  {
    id: "12",
    metricKey: "adsCvrData",
    format: (v) => (v != null ? `${v}%` : "N/A"),
    diffType: "compare",
    compareKey: "cvrCompare",
  },
  {
    id: "13",
    metricKey: "adsCtrData",
    format: (v) => (v != null ? `${v}%` : "N/A"),
    diffType: "compare",
    compareKey: "ctrCompare",
  },
  {
    id: "14",
    metricKey: "organicImpressionsData",
    format: (v) => (v != null ? v.toString() : "N/A"),
    diffType: "difference",
  },
  {
    id: "15",
    metricKey: "organicClicksData",
    format: (v) => (v != null ? v.toString() : "N/A"),
    diffType: "difference",
  },
  {
    id: "16",
    metricKey: "organicCartAddsData",
    format: (v) => (v != null ? v.toString() : "N/A"),
    diffType: "difference",
  },
  {
    id: "17",
    metricKey: "organicUnitsSoldData",
    format: (v) => (v != null ? v.toString() : "N/A"),
    diffType: "difference",
  },
  {
    id: "18",
    metricKey: "organicCvrData",
    format: (v) => (v != null ? `${v}%` : "N/A"),
    diffType: "difference",
  },
  {
    id: "19",
    metricKey: "organicCtrData",
    format: (v) => (v != null ? `${v}%` : "N/A"),
    diffType: "difference",
  },
];

export const createWidgets = async (
  metrics: Metrics,
  compareData: Record<string, number>,
): Promise<IUpdatedWidget[]> => {
  return widgetDefs.map((def) => {
    const data = metrics[def.metricKey];
    const value = def.format(data.value);

    const origData = metrics[def.originalKey ?? def.metricKey];
    const originalValue = safeNumber(origData.value);

    let difference: string;
    if (def.diffType === "difference") {
      difference = String(data.difference);
    } else {
      const key = def.compareKey;
      if (!key) {
        difference = "0";
      } else {
        const baseValue = metrics[def.baseMetricKey ?? def.metricKey].value;
        difference = calculateIncrease(
          baseValue,
          compareData[key],
        ).increase.toString();
      }
    }

    return {
      id: def.id,
      name: data.name,
      value,
      originalValue,
      difference,
    };
  });
};
